import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { buildThemeListenerScript } from '../utils/colorThemeEngine';
import './ResumePreview.css';

/* ─────────────────────────────────────────────────────────
   Constants — A4 at 96 DPI
   ───────────────────────────────────────────────────────── */
const A4_W = 794;   // 210mm at 96 DPI
const A4_H = 1123;  // 297mm at 96 DPI
const DEBOUNCE_MS = 200;

/* ─────────────────────────────────────────────────────────
   Utility: build the full HTML document that goes into the
   iframe.  This is IDENTICAL to what goes to the PDF
   backend, plus a thin measurement helper and the viewport
   zoom trick.
   ───────────────────────────────────────────────────────── */
function buildPreviewDocument(html) {
  if (!html) return '';

  // Page gap must match the outer React component's pageGap constant
  const PAGE_GAP = 24;

  // Inject preview-environment styles right before </head>
  const previewEnvCSS = `
<style id="rg-preview-env">
  /* ── Render at exact A4 width so layout matches Puppeteer viewport ── */
  html {
    width: ${A4_W}px;
    overflow-x: hidden;
  }
  body {
    /* Do NOT set explicit width on body — let it fill the 794px html naturally.
       Setting width: 794px with box-sizing: border-box would subtract padding
       from the content area, but some templates rely on body padding for spacing.
       The html width constraint is sufficient. */
    max-width: ${A4_W}px;
    margin: 0 auto;
    background: white !important;
    /* Let content flow naturally — the outer React wrapper handles pages */
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
    position: relative;
  }
  /* Hide page-number badges & dividers that the old system may have injected */
  .a4-page-number, .a4-page-divider, .a4-page-divider-label { display: none !important; }
  /* Suppress page-break spacers in live preview */
  .rg-page-break-spacer { display: none !important; height: 0 !important; }
  /* Ensure any old A4 containers don't double-wrap */
  .a4-page-container, .a4-page-2 {
    width: auto !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .a4-content-wrapper {
    padding: 0 !important;
    margin: 0 !important;
  }
  /* Page break spacers — creates proper page separation that looks like
     the gap between two separate sheets of paper (Google Docs style) */
  .rg-page-break-spacer {
    width: 100%;
    height: ${PAGE_GAP}px;
    background: linear-gradient(180deg, #d6dbe2 0%, #e0e4ea 50%, #d6dbe2 100%);
    position: relative;
    pointer-events: none;
    flex-shrink: 0;
    display: block;
    clear: both;
    margin: 0;
    padding: 0;
    box-shadow:
      inset 0 2px 4px rgba(0, 0, 0, 0.12),
      inset 0 -2px 4px rgba(0, 0, 0, 0.12);
  }
  .rg-page-break-spacer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(0, 0, 0, 0.15);
  }
  .rg-page-break-spacer::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(0, 0, 0, 0.15);
  }
</style>
<script>
  // Page-break spacer injection + height reporting
  (function(){
    var A4H = ${A4_H};
    var GAP = ${PAGE_GAP};
    var prev = 0;
    var spacersApplied = false;

    function getContentHeight() {
      // Get height without spacers
      var spacers = document.querySelectorAll('.rg-page-break-spacer');
      var spacerH = spacers.length * GAP;
      return document.body.scrollHeight - spacerH;
    }

    function insertSpacers() {
      // Remove old spacers
      var old = document.querySelectorAll('.rg-page-break-spacer');
      old.forEach(function(el){ el.remove(); });

      var rawH = document.body.scrollHeight;
      var pages = Math.ceil(rawH / A4H);
      if (pages <= 1) {
        spacersApplied = true;
        return;
      }

      // Walk through body and find elements that cross page boundaries
      // Insert spacer divs at each A4 page boundary
      var allElements = document.body.querySelectorAll('*');
      var breakPoints = [];
      for (var p = 1; p < pages; p++) {
        breakPoints.push(p * A4H);
      }

      // For each break point, find the element whose top is closest below the break
      breakPoints.forEach(function(bp, idx) {
        // Adjust bp for previously inserted spacers
        var adjustedBP = bp + idx * GAP;
        var bestEl = null;
        var bestDist = Infinity;

        for (var i = 0; i < allElements.length; i++) {
          var el = allElements[i];
          if (el.classList.contains('rg-page-break-spacer')) continue;
          if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') continue;
          var rect = el.getBoundingClientRect();
          var top = rect.top + window.scrollY;
          // Find elements whose top is at or just below the break point
          if (top >= adjustedBP - 5 && top < adjustedBP + 60) {
            var dist = Math.abs(top - adjustedBP);
            if (dist < bestDist) {
              bestDist = dist;
              bestEl = el;
            }
          }
        }

        if (bestEl && bestEl.parentNode) {
          var spacer = document.createElement('div');
          spacer.className = 'rg-page-break-spacer';
          spacer.setAttribute('data-page-break', (idx + 1).toString());
          bestEl.parentNode.insertBefore(spacer, bestEl);
        }
      });

      spacersApplied = true;
    }

    function report(){
      var totalH = document.body.scrollHeight;
      var spacers = document.querySelectorAll('.rg-page-break-spacer');
      var rawH = totalH - (spacers.length * GAP);
      var pages = Math.max(1, Math.ceil(rawH / A4H));
      if(totalH !== prev){
        prev = totalH;
        window.parent.postMessage({type:'rg-height', height: totalH, rawHeight: rawH, pages: pages},'*');
      }
    }

    // Report after fonts ready + images loaded, then insert spacers
    Promise.all([
      document.fonts ? document.fonts.ready : Promise.resolve(),
      new Promise(function(r){ window.addEventListener('load', r); })
    ]).then(function(){
      // Small delay to let layout settle
      setTimeout(function(){
        insertSpacers();
        report();
        // Keep polling briefly in case late layout shifts occur
        var n = 0;
        var iv = setInterval(function(){ report(); if(++n > 10) clearInterval(iv); }, 200);
      }, 100);
    });
    // Fallback: if fonts.ready or load event never fires (e.g. sandboxed iframe,
    // failed Google Font load), still report after 800ms so preview becomes visible
    setTimeout(function(){
      if(prev === 0) {
        insertSpacers();
        report();
      }
    }, 800);
    // Also report on resize (zoom changes)
    window.addEventListener('resize', function(){ report(); });
    // Also on MutationObserver (DOM patches)
    if(window.MutationObserver){
      var debounce = null;
      new MutationObserver(function(mutations){
        // Ignore our own spacer insertions
        var isSpacer = mutations.every(function(m){
          return m.addedNodes && m.addedNodes.length === 1 &&
                 m.addedNodes[0].classList && m.addedNodes[0].classList.contains('rg-page-break-spacer');
        });
        if (isSpacer) return;
        clearTimeout(debounce);
        debounce = setTimeout(function(){
          insertSpacers();
          requestAnimationFrame(report);
        }, 300);
      }).observe(document.documentElement, {childList:true, subtree:true, attributes:true, characterData:true});
    }
  })();
</script>
${buildThemeListenerScript()}`;

  return html.replace(/<\/head>/i, `${previewEnvCSS}\n</head>`);
}

/* ─────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────── */
const ResumePreview = ({
  html,               // Rendered template HTML (same string sent to PDF export)
  loading = false,     // Show loading spinner
  className = '',      // Extra class on root
  colorVars = null,    // { '--primary-color': '#xxx', ... } for real-time updates
}) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const pendingHTMLRef = useRef(null);
  const debounceTimer = useRef(null);

  // State
  const [contentHeight, setContentHeight] = useState(A4_H);
  const [zoom, setZoom] = useState(1);
  const [baseZoom, setBaseZoom] = useState(1);
  const [userZoom, setUserZoom] = useState(1);   // 1 = 100% = page fills container width
  const [ready, setReady] = useState(false);
  const [pageCount, setPageCount] = useState(1);

  /* ── Listen for height reports from iframe ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === 'rg-height') {
        setContentHeight(e.data.height);
        if (e.data.pages) {
          setPageCount(e.data.pages);
        } else {
          setPageCount(Math.max(1, Math.ceil(e.data.height / A4_H)));
        }
        setReady(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  /* ── Fallback: force ready after timeout if height report never arrives ── */
  /* This prevents permanently blank preview when iframe fonts/load event hangs */
  useEffect(() => {
    if (ready) return;
    const fallback = setTimeout(() => {
      if (!ready) {
        setReady(true);
      }
    }, 1500);
    return () => clearTimeout(fallback);
  }, [ready]);

  /* ── Auto-fit: 100% = page width fills container width ── */
  useEffect(() => {
    const computeZoom = () => {
      const container = containerRef.current;
      if (!container) return;
      const pad = 48;
      const availW = container.clientWidth - pad;
      // Width-fit only — user scrolls vertically to see more content
      const widthFit = Math.min(availW / A4_W, 1);
      setBaseZoom(widthFit);
      setZoom(widthFit * userZoom);
    };

    computeZoom();
    const ro = new ResizeObserver(computeZoom);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [userZoom]);

  /* ── Debounced HTML injection ── */
  const injectHTML = useCallback((rawHTML) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      // Retry after a short delay — iframe might not be attached yet
      setTimeout(() => {
        const retryIframe = iframeRef.current;
        if (retryIframe) {
          const document_html = buildPreviewDocument(rawHTML);
          retryIframe.srcdoc = document_html;
        }
      }, 100);
      return;
    }
    const document_html = buildPreviewDocument(rawHTML);
    iframe.srcdoc = document_html;
  }, []);

  const isFirstInjection = useRef(true);

  useEffect(() => {
    if (!html) return;
    // First injection: no debounce — show preview immediately on page load
    if (isFirstInjection.current) {
      isFirstInjection.current = false;
      injectHTML(html);
      return;
    }
    // Subsequent updates: debounce to avoid rapid re-renders while typing
    clearTimeout(debounceTimer.current);
    pendingHTMLRef.current = html;
    debounceTimer.current = setTimeout(() => {
      injectHTML(pendingHTMLRef.current);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceTimer.current);
  }, [html, injectHTML]);

  /* ── Real-time color update via postMessage (no re-render) ── */
  useEffect(() => {
    if (!colorVars) return;
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage({ type: 'rg-theme-update', variables: colorVars }, '*');
  }, [colorVars]);

  /* ── Zoom handlers ── */
  const handleZoomIn = useCallback(() => setUserZoom(z => Math.min(z + 0.1, 1.5)), []);
  const handleZoomOut = useCallback(() => setUserZoom(z => Math.max(z - 0.1, 0.4)), []);
  const handleZoomFit = useCallback(() => setUserZoom(1), []);

  /* ── Derived dimensions ── */
  const scaledW = Math.round(A4_W * zoom);
  const pageGap = 24;
  const scaledPageH = Math.round(A4_H * zoom);
  const scaledGap = Math.round(pageGap * zoom);
  const totalScaledH = pageCount * scaledPageH + (pageCount - 1) * scaledGap;

  /* ── Page overlay shadows ── */
  const pageOverlays = useMemo(() => {
    const pages = [];
    for (let i = 0; i < pageCount; i++) {
      pages.push(
        <div
          key={i}
          className="rg-page-shadow"
          style={{
            width: scaledW,
            height: scaledPageH,
            top: i * (scaledPageH + scaledGap),
          }}
        >
          <div className="rg-page-label">Page {i + 1}</div>
        </div>
      );
    }
    return pages;
  }, [pageCount, scaledW, scaledPageH, scaledGap]);

  /* ── The iframe is rendered at full A4 width, then we use CSS zoom
        on an outer wrapper so font rasterization stays sharp ── */
  return (
    <div className={`rg-preview-root ${className}`} ref={containerRef}>
      {/* ── Zoom toolbar ── */}
      <div className="rg-preview-toolbar">
        <button className="rg-zoom-btn" onClick={handleZoomOut} title="Zoom Out">−</button>
        <span className="rg-zoom-label">{Math.round(userZoom * 100)}%</span>
        <button className="rg-zoom-btn" onClick={handleZoomIn} title="Zoom In">+</button>
        <button className="rg-zoom-btn rg-zoom-fit" onClick={handleZoomFit} title="Fit to Page">⊡</button>
        {pageCount > 1 && (
          <span className="rg-page-indicator">{pageCount} pages</span>
        )}
      </div>

      {/* ── Scrollable viewport ── */}
      <div className="rg-preview-scroll">
        {loading && (
          <div className="rg-preview-loading">
            <div className="rg-spinner" />
            <p>Loading template...</p>
          </div>
        )}
        <div
          className="rg-preview-stage"
          style={{
            width: scaledW,
            minHeight: totalScaledH,
            position: 'relative',
            visibility: loading ? 'hidden' : 'visible',
            height: loading ? 0 : 'auto',
            overflow: loading ? 'hidden' : 'visible',
          }}
        >
          {/* Page shadow overlays */}
          {pageOverlays}

          {/* Scaled wrapper — uses CSS zoom (not transform) for sharp text */}
          <div
            className="rg-preview-zoom-wrapper"
            style={{
              width: A4_W,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
          >
            <iframe
              ref={iframeRef}
              className="rg-preview-iframe"
              title="Resume Preview"
              sandbox="allow-scripts"
              style={{
                width: A4_W,
                height: contentHeight || A4_H,
                opacity: 1,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
